import { createTheme } from '@mui/material/styles';

// Navy scale
export const navy = {
  50:  '#E8EBF0',
  100: '#C5CEDB',
  200: '#9FAEC4',
  300: '#788EAC',
  400: '#5A7599',
  500: '#3B5C87',
  600: '#2E4A6E',
  700: '#213855',
  800: '#1A2D45',
  900: '#1A2332',
} as const;

// Copper scale
export const copper = {
  50:  '#FDF6E3',
  100: '#FAE8B8',
  200: '#F5D889',
  300: '#EFC85A',
  400: '#E5BA35',
  500: '#D4A017',
  600: '#B8860B',
  700: '#9A6F08',
  800: '#7C5906',
  900: '#5E4304',
} as const;

// M70a (E11) — expose the scales above through per-scheme palette tokens so
// mag-HUD colors and copper captions auto-adapt in dark mode. `copperText` is
// the caption/accent color that used to be hardcoded as #9A6F08 across the mag
// components; `mag` carries the D18 glyph colors. With `cssVariables` on, each
// key is also emitted as `--mui-palette-…` for direct use in SVG fills.
export interface MagPalette {
  body: string;
  window: string;
  bar: string;
  spring: string;
  follower: string;
  ink: string;
  /** M93a — the outline around the fitted body silhouette. */
  edge: string;
  /** M93a — panel lines and ribs read off the generated art. */
  line: string;
  /**
   * M94a (D3) — the SHELF half of a caliber card's split bar, opposite
   * `bar` (the loaded half, copper).
   *
   * It has to live here rather than reaching for `primary.main`, which is the
   * obvious choice and the wrong one: the brand INVERTS in dark mode, so
   * `primary.main` is Copper 400 there. A bar drawn copper-on-copper says
   * nothing, which is exactly the single-colour `LinearProgress` failure the
   * split bar exists to replace. This token stays navy in both schemes.
   */
  shelf: string;
  /**
   * M94c (D20) — the "Unverified fit" warning on a build's linked mag set.
   *
   * NOT `warning.main`. MUI's light-mode warning is `#ed6c02`, which is
   * **3.11:1 on a white card** — the plan calls for 4.5:1 on this one because
   * it is a new colour carrying a word a user has to read, not a status band.
   * Measured in the browser, not assumed. Dark mode's `#FFA726` already clears
   * it at 7.97:1, so only the light value moves.
   */
  unverified: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    copperText: { main: string };
    mag: MagPalette;
  }
  interface PaletteOptions {
    copperText?: { main: string };
    mag?: MagPalette;
  }
}

/** CSS-variable accessors for SVG attributes (sx color paths like
 *  'copperText.main' work directly; raw SVG fills need the var string). */
export const magVar = (k: keyof MagPalette) => `var(--mui-palette-mag-${k})`;
export const copperTextVar = 'var(--mui-palette-copperText-main)';

const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'class',
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main:          navy[900],
          light:         navy[600],
          dark:          navy[800],
          contrastText:  '#FFFFFF',
        },
        secondary: {
          main:          copper[600],
          light:         copper[400],
          dark:          copper[700],
          contrastText:  '#FFFFFF',
        },
        background: {
          default: '#F5F6F8',
          paper:   '#FFFFFF',
        },
        divider: '#E2E5EA',
        copperText: { main: copper[700] },
        mag: {
          body:     '#24314A',
          window:   '#FFFFFF',
          bar:      copper[600],
          spring:   navy[300],
          follower: navy[200],
          ink:      navy[900],
          edge:     navy[300],
          line:     navy[500],
          shelf:    navy[600],
          // 5.0:1 on white. `warning.main` (#ed6c02) is 3.11 and fails.
          unverified: '#B45309',
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main:          copper[400],
          light:         copper[300],
          dark:          copper[500],
          contrastText:  navy[900],
        },
        secondary: {
          main:          navy[300],
          light:         navy[200],
          dark:          navy[400],
          contrastText:  '#FFFFFF',
        },
        background: {
          default: '#111820',
          paper:   '#1C2530',
        },
        divider: '#2A3444',
        copperText: { main: copper[400] },
        mag: {
          body:     '#2A3444',
          window:   '#1C2530',
          bar:      copper[400],
          spring:   navy[300],
          follower: navy[200],
          ink:      '#E8EBF0',
          edge:     navy[200],
          line:     navy[400],
          shelf:    navy[300],
          // 7.97:1 on the dark card — MUI's own dark warning already clears it.
          unverified: '#FFA726',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.5px' },
    h2: { fontWeight: 700, letterSpacing: '-0.25px' },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 500 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500 },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiChip: {
      // M69c — outlined is the app's default chip look (it was already the
      // explicit majority; this flips the ~128 unspecified-variant sites to match
      // so category/status tags read consistently). Explicit variant="filled"
      // still wins wherever a filled emphasis is intended (e.g. selected states).
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },
    MuiStepIcon: {
      // Transfer-timeline legibility: MUI paints BOTH the active and the
      // completed step in `primary` (navy in light, copper in dark), so mid-
      // transfer "where I am now" and "what's done" look identical. Make every
      // COMPLETED step green (the done signal) and leave the ACTIVE step on the
      // brand primary, so the three states read distinctly: green done, brand
      // current, grey upcoming.
      styleOverrides: {
        root: {
          '&.Mui-completed': { color: 'var(--mui-palette-success-main)' },
        },
      },
    },
  },
});

export default theme;
